import React from "react";
import {
  CheckCircleIcon,
  PauseIcon,
  TrendingUpIcon,
  XCircleIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const PlanStatusBreakdown = ({ planStats, loading }) => {
  const statusItems = [
    {
      key: "ACTIVE",
      label: "Active Plans",
      icon: CheckCircleIcon,
      color: "bg-green-500",
      textColor: "text-green-700",
      bgColor: "bg-green-50",
    },
    {
      key: "PAUSED",
      label: "Paused Plans",
      icon: PauseIcon,
      color: "bg-purple-500",
      textColor: "text-purple-700",
      bgColor: "bg-purple-50",
    },
    {
      key: "MATURED",
      label: "Matured Plans",
      icon: TrendingUpIcon,
      color: "bg-blue-500",
      textColor: "text-blue-700",
      bgColor: "bg-blue-50",
    },
    {
      key: "AT_RISK",
      label: "At Risk",
      icon: ExclamationIcon,
      color: "bg-orange-500",
      textColor: "text-orange-700",
      bgColor: "bg-orange-50",
      description: "2+ missed payments",
    },
    {
      key: "DEFAULTED",
      label: "Defaulted Plans",
      icon: XCircleIcon,
      color: "bg-red-500",
      textColor: "text-red-700",
      bgColor: "bg-red-50",
    },
  ];

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalPlans = Object.values(planStats).reduce(
    (sum, count) => sum + count,
    0
  );

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Plan Status</h2>
        <span className="text-sm text-gray-500">{totalPlans} total plans</span>
      </div>

      {totalPlans === 0 ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <div className="text-center">
            <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm">No plans found</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {statusItems.map((status) => {
            const count = planStats[status.key] || 0;
            const percentage = totalPlans > 0 ? (count / totalPlans) * 100 : 0;

            return (
              <div
                key={status.key}
                className={`rounded-lg p-3 ${status.bgColor}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className={`${status.color} rounded-full p-1.5 mr-2`}>
                      <status.icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${status.textColor}`}
                      >
                        {status.label}
                      </span>
                      {status.description && (
                        <span className="text-xs text-gray-600 ml-2">
                          ({status.description})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${status.textColor}`}>
                      {count}
                    </div>
                    <div className="text-xs text-gray-600">
                      {percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div
                    className={`${status.color} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}

          {/* Health Score */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Portfolio Health
              </span>
              <div className="flex items-center">
                {(() => {
                  const activeCount = planStats.ACTIVE || 0;
                  const atRiskCount = planStats.AT_RISK || 0;
                  const defaultedCount = planStats.DEFAULTED || 0;

                  const healthyCount = activeCount - atRiskCount;
                  const healthScore =
                    totalPlans > 0
                      ? ((healthyCount / totalPlans) * 100).toFixed(0)
                      : 0;

                  let healthColor = "text-green-600";
                  let healthBg = "bg-green-100";
                  let healthLabel = "Excellent";

                  if (healthScore < 70) {
                    healthColor = "text-red-600";
                    healthBg = "bg-red-100";
                    healthLabel = "Needs Attention";
                  } else if (healthScore < 85) {
                    healthColor = "text-yellow-600";
                    healthBg = "bg-yellow-100";
                    healthLabel = "Fair";
                  } else if (healthScore < 95) {
                    healthColor = "text-blue-600";
                    healthBg = "bg-blue-100";
                    healthLabel = "Good";
                  }

                  return (
                    <>
                      <span
                        className={`text-2xl font-bold ${healthColor} mr-2`}
                      >
                        {healthScore}%
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${healthBg} ${healthColor}`}
                      >
                        {healthLabel}
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Based on active plans with healthy payment patterns
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanStatusBreakdown;
